import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Reveal, SectionLabel, SafeImage } from "@/components/site/bits";
import { api } from "@/lib/api";
import { X, ArrowUpRight, Play } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const BASE_CATEGORIES = ["All", "AI Videos", "Websites", "Mobile Apps", "Automation Projects", "Social Media Campaigns", "CRM Solutions"];

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("All");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/portfolio").then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  const filtered = active === "All" ? items : items.filter((i) => i.category === active);
  const dynamicCats = Array.from(new Set(items.map((i) => i.category).filter(Boolean)));
  const categories = ["All", ...BASE_CATEGORIES.slice(1).filter((c) => dynamicCats.includes(c)), ...dynamicCats.filter((c) => !BASE_CATEGORIES.includes(c))];

  return (
    <section id="portfolio" className="relative py-28 px-6" data-testid="portfolio">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <SectionLabel>Portfolio Showcase</SectionLabel>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight max-w-xl">
              Work that <span className="text-gradient">moves the needle</span>
            </h2>
          </div>
        </Reveal>

        <div className="mt-10 flex flex-wrap gap-3">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
              className={`rounded-full px-5 py-2 text-sm transition-colors ${
                active === c ? "bg-white text-[#0B1220] font-semibold" : "glass text-white/70 hover:text-white"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <motion.div layout className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {filtered.map((p) => (
              <motion.button
                layout
                key={p.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4 }}
                onClick={() => setSelected(p)}
                data-testid={`portfolio-item-${p.id}`}
                className="group text-left overflow-hidden rounded-2xl glass hover:border-[#00E5FF]/40"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <SafeImage src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" fallbackClassName="h-full w-full" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1220] via-[#0B1220]/20 to-transparent" />
                  {p.video && (
                    <span className="absolute inset-0 grid place-items-center" data-testid={`portfolio-video-badge-${p.id}`}>
                      <span className="grid h-14 w-14 place-items-center rounded-full bg-[#00E5FF]/90 text-[#0B1220] shadow-lg backdrop-blur transition-transform group-hover:scale-110">
                        <Play className="h-6 w-6 translate-x-0.5" fill="currentColor" />
                      </span>
                    </span>
                  )}
                  <span className="absolute top-3 left-3 rounded-full bg-[#0B1220]/70 border border-white/10 px-3 py-1 text-xs text-[#00E5FF]">{p.category}</span>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-lg font-semibold">{p.name}</h3>
                    <ArrowUpRight className="h-4 w-4 text-white/40 group-hover:text-[#00E5FF] transition-colors" />
                  </div>
                  <p className="mt-1 text-xs text-white/50">{p.industry}</p>
                  <p className="mt-3 text-sm text-[#00FFB2]">{p.result}</p>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl bg-[#0B1220] border-white/10 text-white p-0 max-h-[88vh] overflow-y-auto" data-testid="portfolio-modal">
          {selected && (
            <div>
              <DialogTitle className="sr-only">{selected.name}</DialogTitle>
              <DialogDescription className="sr-only">Case study details for {selected.name}</DialogDescription>
              <div className="relative aspect-[16/9] bg-black">
                {selected.video ? (
                  <video src={selected.video} poster={selected.image || undefined} controls playsInline
                    className="h-full w-full object-contain bg-black" data-testid="portfolio-modal-video" />
                ) : (
                  <>
                    <SafeImage src={selected.image} alt={selected.name} className="h-full w-full object-cover" fallbackClassName="h-full w-full" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B1220] to-transparent" />
                  </>
                )}
                <button onClick={() => setSelected(null)} className="absolute top-4 right-4 grid h-9 w-9 place-items-center rounded-full bg-black/40 border border-white/10 z-10" data-testid="portfolio-modal-close">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="p-7">
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="rounded-full bg-[#00E5FF]/10 px-3 py-1 text-xs text-[#00E5FF]">{selected.category}</span>
                  <span className="rounded-full bg-white/5 px-3 py-1 text-xs text-white/60">{selected.industry}</span>
                  {(selected.tags || []).map((t) => (
                    <span key={t} className="rounded-full bg-white/5 px-3 py-1 text-xs text-white/60">{t}</span>
                  ))}
                </div>
                <h3 className="font-display text-2xl font-bold">{selected.name}</h3>
                <div className="mt-5 space-y-4 text-sm">
                  <div><span className="text-[#7B61FF] font-semibold">Problem — </span><span className="text-white/70">{selected.problem}</span></div>
                  <div><span className="text-[#00E5FF] font-semibold">Solution — </span><span className="text-white/70">{selected.solution}</span></div>
                  <div><span className="text-[#00FFB2] font-semibold">Result — </span><span className="text-white/70">{selected.result}</span></div>
                </div>
                <Link to={`/portfolio/${selected.id}`} data-testid="portfolio-case-link"
                  className="mt-7 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#7B61FF] px-6 py-3 font-semibold text-[#0B1220] hover:scale-[1.02] transition-transform">
                  Read full case study <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
